import { useState } from 'react';
import styled, { css } from 'styled-components';
import { Box, Card, NoSsr } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { colors, spaces } from '@99designs/emu/settings';
import { useIsExperimentActivated } from '@99designs/experiments';
import { useSite } from '@99designs/spa-common';
import { usePrintProductRecommendationsQuery } from '../../graph';
import PulsingBox from '../../lib/components/PulsingBox';
import PrintRecommendationsLogoSelectorModal from '../PrintRecommendationsLogoSelectorModal';
import { PrintRecommendationItemSkeleton } from './PrintRecommendationItem';
import PrintRecommendationsBannerGeneric from './PrintRecommendationsBannerGeneric';
import PrintRecommendationsBannerPersonalized from './PrintRecommendationsBannerPersonalized';
import { RecommendationsCarouselContainer } from './RecommendationsCarouselContainer';
import { calculateLuminance } from './helpers';

const PrintRecommendationsCard = styled(Card)<{
    prominentColor: string;
    isLabsExperimentEnabled: boolean;
}>`
    position: relative;
    background-color: ${colors.experimental.blue100};
    ${({ prominentColor, isLabsExperimentEnabled }) => {
        if (isLabsExperimentEnabled) {
            const colorLuminance = calculateLuminance(prominentColor);
            return css`
                background-color: ${colorLuminance < 200
                    ? `${prominentColor}10`
                    : colors.experimental.blue100};
            `;
        }
        return;
    }}
    margin-bottom: ${spaces['4bu']};

    ${mediaQuery.only('small')} {
        border-radius: 0;
        margin: -${spaces['1bu']} -${spaces['1bu']} ${spaces['2bu']};
    }
`;

type PrintRecommendationsProps = {
    logoImages:
        | ({
              id: string;
          } & Parameters<typeof PrintRecommendationsLogoSelectorModal>[0]['options'][0])[]
        | undefined;
};

function PrintRecommendationBanner({ logoImages }: PrintRecommendationsProps) {
    const site = useSite();
    const isLabsExperimentEnabled = useIsExperimentActivated('labs', 'enabled');

    const [imageUrl, setImageUrl] = useState(logoImages?.[0]?.previewUrl ?? '');

    const { data, loading } = usePrintProductRecommendationsQuery({
        variables: {
            locale: site.siteConfig.locale.replace('_', '-'),
            imageUrl,
        },
    });

    const recommendedItems = data?.printProductRecommendations.recommendedProducts || [];

    const hasItemsToShow = loading || recommendedItems.length > 0;

    const prominentColor = data?.printProductRecommendations?.prominentColorOfImage ?? '';

    // The Box wrapping the Heading does not get assigned the correct class name but
    // instead gets assigned the exact same class name as the sibling Box after it,
    // hence it gets rendered with the wrong styles
    //
    // For some unknown reason, wrapping this component in NoSsr fixes this, so this
    // seems like it may be an issue or bug with the SSR.
    return (
        <NoSsr>
            <PrintRecommendationsCard
                data-testid="print-recommendations-card"
                appearance="filled"
                prominentColor={prominentColor}
                isLabsExperimentEnabled={isLabsExperimentEnabled}
            >
                {loading ? (
                    <>
                        <Box paddingRight="3bu">
                            <PulsingBox
                                data-testid="print-recommendations-header-skeleton"
                                height="48px"
                            />
                        </Box>
                        <Box display="flex" flexWrap="wrap">
                            <Box
                                display={['none', 'none', 'block']}
                                minWidth="190px"
                                flex="1 1 190px"
                                marginY="1bu"
                                marginRight="1.5bu"
                            >
                                <Box>
                                    <PulsingBox height="32px" />
                                    <PulsingBox height="32px" />
                                </Box>
                            </Box>
                            <RecommendationsCarouselContainer>
                                {Array.from({ length: 4 }, (_, i) => (
                                    <PrintRecommendationItemSkeleton key={i} />
                                ))}
                            </RecommendationsCarouselContainer>
                        </Box>
                    </>
                ) : hasItemsToShow && logoImages ? (
                    <PrintRecommendationsBannerPersonalized
                        logoImages={logoImages}
                        data={data}
                        imageUrl={imageUrl}
                        setImageUrl={setImageUrl}
                    />
                ) : (
                    <PrintRecommendationsBannerGeneric />
                )}
            </PrintRecommendationsCard>
        </NoSsr>
    );
}

export default PrintRecommendationBanner;
